// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

// D-UI-3 c7 — Screening & Hold data through TanStack Query (E-02). The screen used to fetch with
// useEffect + Promise.all, so a failed load left it on "Loading…" or on a stale list with no way to retry.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getScreeningRules, listScreeningHolds, resolveScreeningHold, type ScreeningHoldRow } from '../../api/client'
import { clinicalKeys } from './carerxClinical'

export const holdKeys = {
  list: ['carerx', 'screening-holds'] as const,
  rules: ['carerx', 'screening-hold-rules'] as const,
}

export function useScreeningHolds() {
  return useQuery({ queryKey: holdKeys.list, queryFn: listScreeningHolds })
}

/** The rule reference changes only when the workbook is re-seeded. */
export function useScreeningRules(enabled: boolean) {
  return useQuery({ queryKey: holdKeys.rules, queryFn: getScreeningRules, enabled, staleTime: 10 * 60_000 })
}

/** The resolution note as stored and audited: the pharmacist's words + the prescriber-notification answer. */
export function resolutionText(note: string, prescriberNotified: boolean): string {
  return `${note.trim()}\nPrescriber notified: ${prescriberNotified ? 'Yes' : 'No'}`
}

const alreadyResolved = (e: unknown) =>
  typeof e === 'object' && e !== null && (e as { status?: number }).status === 409
  && (e as { body?: { error?: string } }).body?.error === 'already_resolved'

export interface ResolveOutcome { resolved: number; total: number; error?: unknown }

/**
 * Resolves every OPEN hold on one therapy, one audited request each, in order. Stops at the first failure
 * so the rest stay standing (a half-cleared therapy is worse than a clearly partial one) and says how far
 * it got. The list and the stage counts refresh either way.
 */
export function useResolveHolds() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (v: { row: ScreeningHoldRow; resolution: string; prescriberNotified: boolean }): Promise<ResolveOutcome> => {
      const ids = v.row.holds.filter(h => h.status !== 'resolved').map(h => h.id)
      let resolved = 0
      for (const id of ids) {
        try {
          await resolveScreeningHold(id, { resolution: v.resolution, status: 'resolved', prescriber_notified: v.prescriberNotified })
          resolved++
        } catch (error) {
          // Already resolved (by this screen's earlier attempt, or by a colleague): done, not a failure.
          if (alreadyResolved(error)) { resolved++; continue }
          return { resolved, total: ids.length, error }
        }
      }
      return { resolved, total: ids.length }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: holdKeys.list })
      void qc.invalidateQueries({ queryKey: clinicalKeys.counts })
    },
  })
}
